import { Button, Link, Spinner } from '@heroui/react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useAutoAuthMutation } from '@/patient-app-common/api/authApi';
import {
  setProviderMode,
  setDirectMode,
  setToken,
} from '@/patient-app-common/slices/authSlice';
import { getURLParam } from '@/patient-app-common/utils';
import { useAppSelector } from '@/redux/hooks';
import { setEhrMode } from '@/redux/reducers/webAppSlice';

interface AutoAuthProps {
  partialExpiryRedirect?: string;
}

export function AutoAuth({ partialExpiryRedirect }: AutoAuthProps) {
  const { route } = useAppSelector(({ app }) => app);
  const { token: savedToken } = useAppSelector(({ auth }) => auth);

  const { t, i18n } = useTranslation('login');
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, eventId } = useParams<{ token: string; eventId: string }>();
  const [autoAuth] = useAutoAuthMutation();

  const [error, setError] = useState('');

  useEffect(() => {
    const initLocale = getURLParam(location.search, 'lang') || 'en';
    dayjs.locale(initLocale);
    i18n.changeLanguage(initLocale);
  }, []);

  useLayoutEffect(() => {
    const directMode = getURLParam(location.search, 'mode') === 'direct';

    if (directMode) {
      dispatch(setDirectMode(true));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    const providerMode = getURLParam(location.search, 'mode') === 'provider';

    if (providerMode) {
      dispatch(setProviderMode(true));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    const ehrMode = getURLParam(location.search, 'mode') === 'ehr_login';

    if (ehrMode) {
      dispatch(setEhrMode(true));
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    dispatch(setToken(token));
  }, [token]);

  useEffect(() => {
    if (route === null || !savedToken) return;
    verifyToken();
  }, [route, savedToken]);

  const verifyToken = async () => {
    setError('');

    const { pathname, state } = getURL();

    try {
      const result = await autoAuth({
        mode: getURLParam(location.search, 'mode') || undefined,
      }).unwrap();

      if (result.success) {
        navigate(pathname, { state });
      } else {
        catchError(result.error);
      }
    } catch (e) {
      catchError(e);
    }
  };

  const catchError = (error: unknown) => {
    const hasError: unknown = error;
    const fetchError: FetchBaseQueryError = hasError as FetchBaseQueryError;
    const expiryType = getExpiryType(fetchError);

    if (
      partialExpiryRedirect &&
      fetchError.status === 401 &&
      expiryType === 'partially_expired'
    ) {
      navigate(partialExpiryRedirect, { replace: true });
      return;
    }

    setError(getErrorMessage(fetchError));
  };

  const getExpiryType = (fetchError: FetchBaseQueryError) => {
    if (typeof fetchError.data !== 'object' || fetchError.data === null) {
      return undefined;
    }

    const data = fetchError.data as { expiry_type?: string };
    return data.expiry_type;
  };

  const getErrorMessage = (fetchError: FetchBaseQueryError) => {
    switch (fetchError.status) {
      case 401:
        return t('autoauth.error.invalid');

      case 'FETCH_ERROR':
      case 'TIMEOUT_ERROR':
        return t('common.error.fetch');

      case 'PARSING_ERROR':
        return t('common.error.server');

      default:
        return t('common.error.server');
    }
  };

  const getURL = () => {
    const mode = getURLParam(location.search, 'mode');
    let pathname = '/';
    let state = {};

    switch (mode) {
      case 'login':
        pathname = '/';
        break;
      case 'education': {
        const eduId = getURLParam(location.search, 'education_id');
        pathname = `/education/${eduId}`;
        break;
      }
      case 'survey': {
        const procedureId = getURLParam(location.search, 'procedure_id');
        pathname = `/tracker/${procedureId}`;

        if (eventId !== '0') {
          state = { eventId };
        }
        break;
      }
      case 'direct':
      case 'provider': {
        const trackerId = getURLParam(location.search, 'tracker');
        pathname = `/tracker/${trackerId}`;
        if (eventId) {
          state = { eventId };
        }
        break;
      }

      default:
    }

    return { pathname, state };
  };

  if (error) {
    return (
      <div className="flex flex-row h-[100dvh] justify-center">
        <div className="flex flex-col justify-center">
          <h1 className="text-lg">{error}</h1>
          <Button
            className="mt-4 mx-auto"
            color="primary"
            onPress={verifyToken}
          >
            {t('common.action.retry')}
          </Button>
          <Button
            as={Link}
            href={`https://ca.seamless.md/education/241780?lang=${i18n.language}`}
            isExternal
            className="mt-4 mx-auto"
            color="primary"
          >
            {t('common.link.help')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row h-[100dvh] justify-center">
      <div className="flex flex-col justify-center ">
        <Spinner className="m-4" color="primary" size="lg" />
        <h1 className="text-lg">{t('autoauth.title')}</h1>
      </div>
    </div>
  );
}
